import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CpuChipIcon,
  CircleStackIcon,
  ServerIcon,
  CloudIcon,
  SignalIcon,
  ChartBarIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  XMarkIcon,
  ArrowsPointingOutIcon,
  GlobeAltIcon,
  BoltIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import axiosInstance from '../utils/axios';
import { format } from 'date-fns';

// Health status colors and icons
const healthStatusConfig = {
  excellent: {
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    icon: CheckCircleIcon,
    label: 'Excellent'
  },
  good: {
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    icon: CheckCircleIcon,
    label: 'Good'
  },
  fair: {
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    icon: InformationCircleIcon,
    label: 'Fair'
  },
  poor: {
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    icon: ExclamationCircleIcon,
    label: 'Poor'
  },
  critical: {
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    icon: ExclamationCircleIcon,
    label: 'Critical'
  }
};

const MetricBar = ({ label, value, max = 100, color, threshold = { warning: 70, critical: 90 } }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const barColor = percentage >= threshold.critical ? 'bg-red-500' :
                   percentage >= threshold.warning ? 'bg-yellow-500' :
                   color || 'bg-green-500';
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center text-sm">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium text-gray-900">{value}%</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className={`h-full ${barColor} rounded-full`}
        />
      </div>
    </div>
  );
};

const AWSInfoCard = ({ label, value, icon: Icon, color = 'text-gray-600' }) => (
  <div className="bg-gray-50 rounded-lg p-3">
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-4 h-4 ${color}`} />
      <span className="text-xs text-gray-500">{label}</span>
    </div>
    <p className="text-sm font-medium text-gray-900">{value || 'N/A'}</p>
  </div>
);

const SystemHealth = ({ onExpand }) => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    fetchHealthData();
    const interval = setInterval(fetchHealthData, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchHealthData = async () => {
    try {
      const response = await axiosInstance.get('/system-health');
      if (response.data.success) {
        setHealthData(response.data);
        setLastUpdate(new Date());
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch system health:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="space-y-3">
            <div className="h-2 bg-gray-200 rounded"></div>
            <div className="h-2 bg-gray-200 rounded"></div>
            <div className="h-2 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !healthData) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">System Health</h3>
          <ExclamationCircleIcon className="w-6 h-6 text-red-500" />
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">
            {error || 'Unable to fetch system health data'}
          </p>
          <button
            onClick={fetchHealthData}
            className="mt-2 text-sm text-red-700 hover:text-red-800 font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { health, system, aws, application, summary } = healthData;
  const status = healthStatusConfig[health?.status] || healthStatusConfig.fair;
  const StatusIcon = status.icon;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-lg shadow-sm border border-gray-200"
    >
      {/* Header */}
      <div className={`p-4 border-b ${status.borderColor} ${status.bgColor}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <StatusIcon className={`w-6 h-6 ${status.color}`} />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">System Health</h3>
              <p className={`text-sm ${status.color} font-medium`}>
                {status.label} - Score: {health?.score}/100
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchHealthData}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <ArrowPathIcon className="w-4 h-4" />
            </button>
            <button
              onClick={onExpand}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="View Details"
            >
              <ArrowsPointingOutIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Quick Metrics */}
      <div className="p-4 space-y-3">
        <MetricBar 
          label="CPU Usage" 
          value={parseInt(summary?.cpu) || 0}
          threshold={{ warning: 70, critical: 85 }}
        />
        <MetricBar 
          label="Memory Usage" 
          value={parseInt(summary?.memory) || 0}
          threshold={{ warning: 80, critical: 90 }}
        />
        <MetricBar 
          label="Disk Usage" 
          value={parseInt(summary?.disk) || 0}
          threshold={{ warning: 75, critical: 90 }}
        />
        
        {/* System Load Indicator */}
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            <BoltIcon className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-600">Load Average</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">1m:</span>
            <span className="font-medium text-gray-900">{system?.load?.['1min'] || 'N/A'}</span>
            <span className="text-gray-500">5m:</span>
            <span className="font-medium text-gray-900">{system?.load?.['5min'] || 'N/A'}</span>
          </div>
        </div>
        
        {/* Network Traffic Indicator */}
        {summary?.network?.primaryInterface && (
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <SignalIcon className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">Network ({summary.network.primaryInterface.name})</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <div className="flex items-center gap-1">
                <ArrowDownIcon className="w-3 h-3 text-green-500" />
                <span className="font-medium text-gray-900">{summary.network.primaryInterface.rx_rate}</span>
              </div>
              <div className="flex items-center gap-1">
                <ArrowUpIcon className="w-3 h-3 text-blue-500" />
                <span className="font-medium text-gray-900">{summary.network.primaryInterface.tx_rate}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* AWS Info */}
      {aws?.metadata && (
        <div className="px-4 pb-4">
          <div className="border-t border-gray-200 pt-3">
            <div className="flex items-center gap-2 mb-3">
              <CloudIcon className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">AWS Infrastructure</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <AWSInfoCard 
                label="Instance Type" 
                value={aws.metadata.instanceType}
                icon={ServerIcon}
                color="text-blue-600"
              />
              <AWSInfoCard 
                label="Region" 
                value={aws.metadata.region}
                icon={GlobeAltIcon}
                color="text-green-600"
              />
            </div>
          </div>
        </div>
      )}

      {/* Security Alert Indicator */}
      {healthData.security?.alerts && healthData.security.alerts.length > 0 && (
        <div className="px-4 pb-4">
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ExclamationCircleIcon className="w-5 h-5 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-800">
                {healthData.security.alerts.length} Security Alert{healthData.security.alerts.length > 1 ? 's' : ''} Detected
              </span>
            </div>
            <button
              onClick={onExpand}
              className="text-xs text-yellow-700 hover:text-yellow-900 font-medium"
            >
              View Details →
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 rounded-b-lg">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <ClockIcon className="w-3 h-3" />
            <span>Uptime: {summary?.uptime || 'N/A'}</span>
          </div>
          <div>
            Last updated: {format(lastUpdate, 'HH:mm:ss')}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Add missing import
const ArrowPathIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);


export default SystemHealth;